import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, Clock, BookOpen, Heart, HeartOff, CheckCircle2,
  Brain, Wind, Target, Flame, Music, Eye, Sparkles, Leaf, Lock, ChevronRight
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AudioPlayer from "@/components/AudioPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  meditation: Brain, breathwork: Wind, chakra: Target, kundalini: Flame,
  mantras: Music, yoga: Heart, consciousness: Eye, ancient_secrets: Sparkles, mindfulness: Leaf,
};

const DIFFICULTY_COLORS = {
  beginner: "bg-[#8FA69A]/20 text-[#2C4A3B]",
  intermediate: "bg-[#D4AF37]/20 text-[#8B7520]",
  advanced: "bg-[#C17B5F]/20 text-[#8B4530]",
};

export default function PracticeDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [practice, setPractice] = useState(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [sessionActive, setSessionActive] = useState(false);
  const [showComplete, setShowComplete] = useState(false);

  useEffect(() => {
    axios.get(`${API}/practices/${id}`).then((r) => {
      setPractice(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));

    if (user) {
      axios.get(`${API}/favorites`).then((r) => {
        setIsFavorited(r.data.some((f) => f.id === id));
      }).catch(console.error);
    }
  }, [id, user]);

  const toggleFavorite = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      const res = await axios.post(`${API}/favorites/${id}`);
      setIsFavorited(res.data.favorited);
      toast(res.data.favorited ? "Added to your sacred collection" : "Removed from collection");
    } catch {
      toast.error("Failed to update favorites");
    }
  };

  const startSession = () => {
    if (!user) { navigate("/login"); return; }
    setSessionActive(true);
    setCurrentStep(0);
  };

  const nextStep = () => {
    if (currentStep < practice.steps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      completeSession();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  const completeSession = async () => {
    try {
      await axios.post(`${API}/progress`, {
        practice_id: practice.id,
        practice_title: practice.title,
        category: practice.category,
        duration_minutes: practice.duration_minutes,
        notes: "",
      });
      setShowComplete(true);
      setSessionActive(false);
      toast.success("Practice complete! Your progress has been recorded.");
    } catch {
      toast.error("Failed to record progress");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="practice-loading">
        <div className="w-8 h-8 rounded-full border-2 border-[#8FA69A] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!practice) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="practice-not-found">
        <p className="text-[#737E79]">Practice not found.</p>
      </div>
    );
  }

  const Icon = CATEGORY_ICONS[practice.category] || Sparkles;
  const stepProgress = practice.steps.length > 0 ? ((currentStep + 1) / practice.steps.length) * 100 : 0;

  return (
    <main className="min-h-screen" data-testid="practice-detail-page">
      {/* Hero with image */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img
          src={practice.image_url}
          alt={practice.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A221E]/80 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <Link
            to="/library"
            data-testid="back-to-library"
            className="inline-flex items-center gap-1 text-white/70 text-sm mb-4 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Back to Library
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <Badge className={`${DIFFICULTY_COLORS[practice.difficulty]} border-0 rounded-full text-xs`}>
              {practice.difficulty}
            </Badge>
            {practice.is_secret && (
              <Badge className="bg-[#D4AF37]/30 text-[#D4AF37] border-0 rounded-full text-xs flex items-center gap-1">
                <Lock className="w-3 h-3" strokeWidth={1.5} />
                Secret Technique
              </Badge>
            )}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-light text-white tracking-tight">
            {practice.title}
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-8 md:py-12">
        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 mb-8">
          <div className="flex items-center gap-2 text-sm text-[#737E79]">
            <Icon className="w-4 h-4" strokeWidth={1.5} />
            <span className="capitalize">{practice.category.replace("_", " ")}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#737E79]">
            <BookOpen className="w-4 h-4" strokeWidth={1.5} />
            {practice.tradition}
          </div>
          <div className="flex items-center gap-2 text-sm text-[#737E79]">
            <Clock className="w-4 h-4" strokeWidth={1.5} />
            {practice.duration_minutes} minutes
          </div>
          <button
            onClick={toggleFavorite}
            data-testid="toggle-favorite-btn"
            className="ml-auto flex items-center gap-2 px-4 py-2 rounded-full border border-[#E3DDD2] text-sm text-[#737E79] hover:bg-[#EAE2D6] transition-all duration-300"
          >
            {isFavorited ? (
              <><Heart className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" strokeWidth={1.5} /> Saved</>
            ) : (
              <><HeartOff className="w-4 h-4" strokeWidth={1.5} /> Save</>
            )}
          </button>
        </div>

        {/* Description */}
        <section className="mb-10" data-testid="practice-description">
          <h2 className="font-heading text-xl font-medium text-[#1A221E] mb-3">Overview</h2>
          <p className="text-base text-[#737E79] leading-relaxed">{practice.overview}</p>
        </section>

        {/* Audio Player */}
        <section className="mb-10" data-testid="audio-section">
          <AudioPlayer practiceId={practice.id} practiceTitle={practice.title} />
        </section>

        {/* Session / Steps */}
        {!sessionActive ? (
          <>
            {/* Steps Preview */}
            <section className="mb-10" data-testid="steps-preview">
              <h2 className="font-heading text-xl font-medium text-[#1A221E] mb-4">
                Steps ({practice.steps.length})
              </h2>
              <div className="space-y-3">
                {practice.steps.map((step, i) => (
                  <div
                    key={i}
                    className="flex gap-4 p-4 rounded-2xl bg-white border border-[#E3DDD2]"
                  >
                    <div className="w-7 h-7 rounded-full bg-[#8FA69A]/10 flex items-center justify-center shrink-0">
                      <span className="text-xs font-medium text-[#8FA69A]">{i + 1}</span>
                    </div>
                    <p className="text-sm text-[#1A221E] leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Benefits */}
            <section className="mb-10" data-testid="practice-benefits">
              <h2 className="font-heading text-xl font-medium text-[#1A221E] mb-4">Benefits</h2>
              <div className="flex flex-wrap gap-2">
                {practice.benefits.map((b, i) => (
                  <Badge
                    key={i}
                    className="bg-[#8FA69A]/10 text-[#2C4A3B] border-0 rounded-full px-3 py-1.5 text-sm font-normal"
                  >
                    {b}
                  </Badge>
                ))}
              </div>
            </section>

            {/* Tips */}
            {practice.tips.length > 0 && (
              <section className="mb-10" data-testid="practice-tips">
                <h2 className="font-heading text-xl font-medium text-[#1A221E] mb-4">Tips</h2>
                <div className="space-y-2">
                  {practice.tips.map((tip, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <Sparkles className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" strokeWidth={1.5} />
                      <p className="text-sm text-[#737E79] leading-relaxed">{tip}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Start Button */}
            <button
              onClick={startSession}
              data-testid="start-practice-btn"
              className="w-full py-4 bg-[#8FA69A] text-white rounded-2xl text-base font-medium transition-all duration-300 hover:bg-[#2C4A3B] hover:shadow-lg"
            >
              Begin Practice Session
            </button>
          </>
        ) : (
          /* Active Session */
          <section data-testid="active-session">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-[#737E79]">
                  Step {currentStep + 1} of {practice.steps.length}
                </span>
                <span className="text-xs text-[#8FA69A] font-medium">
                  {Math.round(stepProgress)}%
                </span>
              </div>
              <Progress value={stepProgress} className="h-2 bg-[#EAE2D6]" />
            </div>

            <div className="p-8 rounded-2xl bg-white border border-[#E3DDD2] mb-6 min-h-[200px] flex items-center">
              <div>
                <div className="w-10 h-10 rounded-full bg-[#8FA69A] flex items-center justify-center mb-4">
                  <span className="text-sm font-medium text-white">{currentStep + 1}</span>
                </div>
                <p className="text-lg text-[#1A221E] leading-relaxed font-heading">
                  {practice.steps[currentStep]}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={prevStep}
                disabled={currentStep === 0}
                data-testid="prev-step-btn"
                className="flex-1 py-3 rounded-2xl border border-[#E3DDD2] text-[#737E79] text-sm font-medium disabled:opacity-30 hover:bg-[#EAE2D6] transition-all duration-300"
              >
                Previous
              </button>
              <button
                onClick={nextStep}
                data-testid="next-step-btn"
                className="flex-1 py-3 rounded-2xl bg-[#8FA69A] text-white text-sm font-medium hover:bg-[#2C4A3B] transition-all duration-300 flex items-center justify-center gap-2"
              >
                {currentStep < practice.steps.length - 1 ? (
                  <>Next Step <ChevronRight className="w-4 h-4" strokeWidth={1.5} /></>
                ) : (
                  <>Complete <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /></>
                )}
              </button>
            </div>
          </section>
        )}
      </div>

      {/* Completion Dialog */}
      <Dialog open={showComplete} onOpenChange={setShowComplete}>
        <DialogContent className="rounded-2xl bg-white border-[#E3DDD2] max-w-md" data-testid="completion-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-center text-[#1A221E]">
              Practice Complete
            </DialogTitle>
            <DialogDescription className="text-center text-[#737E79] mt-2">
              You have completed <span className="text-[#8FA69A] font-medium">{practice.title}</span>.
              Your spiritual journey deepens with each practice.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-4">
            <Link
              to="/dashboard"
              data-testid="go-to-dashboard-btn"
              className="w-full py-3 bg-[#8FA69A] text-white rounded-full text-sm font-medium text-center transition-all duration-300 hover:bg-[#2C4A3B]"
            >
              View Your Journey
            </Link>
            <button
              onClick={() => { setShowComplete(false); setSessionActive(false); setCurrentStep(0); }}
              data-testid="close-completion-btn"
              className="w-full py-3 border border-[#E3DDD2] text-[#737E79] rounded-full text-sm font-medium transition-all duration-300 hover:bg-[#EAE2D6]"
            >
              Stay Here
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
